import { clear, el } from '../utils/dom.js';
import { formatPace, formatTime } from '../ultra/ultraPacing.js';
import { formatComparisonRow } from '../analysis/comparison.js';

/**
 * Render the comparison summary tiles.
 */
export function renderComparisonSummary(container, comparison) {
  clear(container);
  if (!comparison?.summary) return;

  const { summary } = comparison;

  const root = el('div', { class: 'comparison-summary' });
  root.appendChild(el('h3', { text: 'Planned vs Actual Summary' }));

  const tiles = el('div', { class: 'ultra-tiles' });

  const tileItems = [
    {
      value: formatTime(summary.predictedFinish),
      label: 'Predicted Finish'
    },
    {
      value: formatTime(summary.actualFinish),
      label: 'Actual Finish'
    },
    {
      value: formatDelta(summary.totalTimeDelta),
      label: 'Time Delta',
      sublabel: `${summary.totalTimeDeltaPct >= 0 ? '+' : ''}${summary.totalTimeDeltaPct.toFixed(1)}%`,
      colorClass: summary.totalTimeDelta > 0 ? 'delta-behind' : 'delta-ahead'
    },
    {
      value: summary.predictedCohort ?? '-',
      label: 'Predicted Cohort'
    },
    {
      value: summary.effectiveCohort ?? '-',
      label: 'Effective Cohort',
      sublabel: 'Based on actual performance'
    },
    {
      value: summary.totalStoppedTime > 0 ? formatTime(summary.totalStoppedTime) : '-',
      label: 'Stopped Time',
      sublabel: 'Aid stations / breaks'
    }
  ];

  for (const item of tileItems) {
    const tile = el('div', { class: `ultra-tile ${item.colorClass ?? ''}` }, [
      el('div', { class: 'ultra-tile-value', text: item.value }),
      el('div', { class: 'ultra-tile-label', text: item.label }),
      item.sublabel ? el('div', { class: 'ultra-tile-sublabel', text: item.sublabel }) : null
    ]);
    tiles.appendChild(tile);
  }
  root.appendChild(tiles);

  const ratingGrid = el('div', { class: 'comparison-rating-grid' });
  ratingGrid.appendChild(renderRatingBadge('Ahead', summary.sectionsAhead, '#28a745'));
  ratingGrid.appendChild(renderRatingBadge('On Target', summary.sectionsOnTarget, '#ffc107'));
  ratingGrid.appendChild(renderRatingBadge('Behind', summary.sectionsBehind, '#fd7e14'));
  ratingGrid.appendChild(renderRatingBadge('Sig. Behind', summary.sectionsSignificantlyBehind, '#dc3545'));
  root.appendChild(ratingGrid);

  if (summary.fadeAnalysis) {
    const fade = summary.fadeAnalysis;
    const fadeInfo = el('div', { class: 'comparison-fade-info' });
    if (fade.fadeStartPct != null) {
      fadeInfo.appendChild(el('p', {
        text: `Fade detected at ~${fade.fadeStartPct.toFixed(0)}% of the race (3+ consecutive sections behind prediction)`
      }));
    } else {
      fadeInfo.appendChild(el('p', { text: 'No consistent fade pattern detected' }));
    }
    root.appendChild(fadeInfo);
  }

  container.appendChild(root);
}

function renderRatingBadge(label, count, color) {
  const badge = el('div', { class: 'comparison-rating-badge' });
  const countEl = el('div', { class: 'comparison-rating-count', text: String(count) });
  countEl.style.color = color;
  badge.appendChild(countEl);
  badge.appendChild(el('div', { class: 'comparison-rating-label', text: label }));
  return badge;
}

/**
 * Render the comparison table showing per-section data.
 */
export function renderComparisonTable(tableEl, comparison) {
  clear(tableEl);
  if (!comparison?.sections?.length) return;

  const thead = el('thead');
  const headerRow = el('tr');
  const headers = [
    'Section', 'Distance', 'Gradient', 'Type',
    'Pred. Pace', 'Actual Pace', 'Pred. Time', 'Actual Time',
    'Delta', 'Cum. Delta', 'HR', 'Rating'
  ];
  for (const h of headers) {
    headerRow.appendChild(el('th', { text: h }));
  }
  thead.appendChild(headerRow);
  tableEl.appendChild(thead);

  const tbody = el('tbody');
  for (const section of comparison.sections) {
    const row = formatComparisonRow(section);
    const tr = el('tr');
    tr.classList.add(`comparison-${row.rating}`);

    const cells = [
      row.sectionNumber,
      `${row.distanceKm.toFixed(2)} km`,
      `${row.gradient.toFixed(1)}%`,
      row.sectionType,
      row.predictedPaceStr + '/km',
      row.actualPaceStr + '/km',
      row.predictedTimeStr,
      row.actualTimeStr,
      row.timeDeltaStr,
      row.cumulativeDeltaStr,
      row.hrStr,
      row.ratingLabel
    ];

    for (let i = 0; i < cells.length; i++) {
      const td = el('td', { text: String(cells[i]) });
      if (i >= 4) td.classList.add('numeric');
      if (i === cells.length - 1) {
        td.style.color = row.ratingColor;
        td.style.fontWeight = 'bold';
      }
      tr.appendChild(td);
    }
    tbody.appendChild(tr);
  }
  tableEl.appendChild(tbody);
}

/**
 * Draw the cumulative time delta (fade) chart on a canvas.
 */
export function drawFadeChart(canvas, comparison) {
  if (!canvas || !comparison?.sections?.length) return;

  const ctx = canvas.getContext('2d');
  const sections = comparison.sections;

  const width = 800;
  const height = 300;
  const padding = { top: 30, right: 30, bottom: 50, left: 60 };
  canvas.width = width;
  canvas.height = height;

  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);

  let cumulativeDist = 0;
  const points = [{ x: 0, y: 0 }];
  for (const section of sections) {
    cumulativeDist += section.distanceKm;
    points.push({ x: cumulativeDist, y: section.cumulativeTimeDelta });
  }

  const maxDist = cumulativeDist || 1;
  const maxDelta = Math.max(1, Math.max(...points.map(p => Math.abs(p.y))));

  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  function toCanvasX(dist) { return padding.left + (dist / maxDist) * chartW; }
  function toCanvasY(delta) { return padding.top + chartH / 2 - (delta / maxDelta) * (chartH / 2); }

  // Grid lines
  ctx.strokeStyle = '#e9ecef';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(padding.left, toCanvasY(0));
  ctx.lineTo(width - padding.right, toCanvasY(0));
  ctx.stroke();

  // Shade regions
  ctx.fillStyle = 'rgba(40, 167, 69, 0.05)';
  ctx.fillRect(padding.left, padding.top, chartW, chartH / 2);
  ctx.fillStyle = 'rgba(220, 53, 69, 0.05)';
  ctx.fillRect(padding.left, padding.top + chartH / 2, chartW, chartH / 2);

  // Delta line
  ctx.strokeStyle = '#007bff';
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  for (let i = 0; i < points.length; i++) {
    const cx = toCanvasX(points[i].x);
    const cy = toCanvasY(points[i].y);
    if (i === 0) ctx.moveTo(cx, cy);
    else ctx.lineTo(cx, cy);
  }
  ctx.stroke();

  // Fill under/over zero
  ctx.globalAlpha = 0.15;
  ctx.beginPath();
  ctx.moveTo(toCanvasX(points[0].x), toCanvasY(0));
  for (const p of points) ctx.lineTo(toCanvasX(p.x), toCanvasY(p.y));
  ctx.lineTo(toCanvasX(points[points.length - 1].x), toCanvasY(0));
  ctx.closePath();
  const lastDelta = points[points.length - 1].y;
  ctx.fillStyle = lastDelta >= 0 ? '#dc3545' : '#28a745';
  ctx.fill();
  ctx.globalAlpha = 1;

  // Points
  for (let i = 1; i < points.length; i++) {
    const cx = toCanvasX(points[i].x);
    const cy = toCanvasY(points[i].y);
    ctx.fillStyle = points[i].y > 0 ? '#dc3545' : '#28a745';
    ctx.beginPath();
    ctx.arc(cx, cy, 3, 0, Math.PI * 2);
    ctx.fill();
  }

  // Labels
  ctx.fillStyle = '#333';
  ctx.font = '11px sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('Distance (km)', width / 2, height - 5);

  ctx.save();
  ctx.translate(14, height / 2);
  ctx.rotate(-Math.PI / 2);
  ctx.fillText('Cumulative Delta (min)', 0, 0);
  ctx.restore();

  // Axis labels
  ctx.textAlign = 'left';
  ctx.font = '10px sans-serif';
  ctx.fillStyle = '#28a745';
  ctx.fillText('Ahead', padding.left + 4, padding.top + 14);
  ctx.fillStyle = '#dc3545';
  ctx.fillText('Behind', padding.left + 4, height - padding.bottom - 4);

  // X-axis tick marks
  ctx.fillStyle = '#666';
  ctx.textAlign = 'center';
  const xStep = Math.ceil(maxDist / 8);
  for (let d = 0; d <= maxDist; d += xStep) {
    const cx = toCanvasX(d);
    ctx.fillText(d.toFixed(0), cx, height - padding.bottom + 16);
  }

  // Y-axis tick marks
  ctx.textAlign = 'right';
  const yStep = Math.ceil(maxDelta / 3);
  for (let v = -maxDelta; v <= maxDelta; v += yStep) {
    const cy = toCanvasY(v);
    ctx.fillText(formatDelta(v), padding.left - 6, cy + 4);
  }

  // Title
  ctx.fillStyle = '#333';
  ctx.font = 'bold 13px sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('Cumulative Time Delta vs Prediction', width / 2, 16);
}

function formatDelta(minutes) {
  const sign = minutes >= 0 ? '+' : '-';
  const abs = Math.abs(minutes);
  if (abs >= 60) {
    const h = Math.floor(abs / 60);
    const m = Math.round(abs % 60);
    return `${sign}${h}h${String(m).padStart(2, '0')}`;
  }
  return `${sign}${Math.round(abs)}m`;
}
